import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

import AudioPlayer from "@/components/AudioPlayer";
import AuthModal from "@/components/auth/AuthModal";
import PolicyConsentModal from "@/components/legal/PolicyConsentModal";
import EmailVerificationGateContainer from "@/components/auth/EmailVerificationGateContainer";
import PolicyUpdateBanner from "@/components/legal/PolicyUpdateBanner";
import CartDrawer from "@/components/CartDrawer";
import GlobalToastObserver from "@/components/GlobalToastObserver";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import HeaderNav from "@/components/nav/HeaderNav";
import Providers from "@/components/providers";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import {
  useAudioPlayerQueue,
  useAudioPlayerState,
} from "@/components/providers/AudioPlayerProvider/useAudioPlayer";
import { analytics, COOKIE_CONSENT_CONFIG } from "@/lib/analytics";
import { saveMostRecentRoute } from "@/lib/storage-utils";
import { datadogRum } from "@datadog/browser-rum";
import { useStatsigUser } from "@statsig/react-bindings";
import {
  createRootRoute,
  HeadContent,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import Userback from "@userback/widget";
import { useCallback, useEffect, useRef } from "react";
import { getUserbackInstance, setUserbackInstance } from "@/hooks/useUserback";
import {
  organizationJsonLd,
  seoHead,
  SITE_DESCRIPTION,
  SITE_TITLE,
  websiteJsonLd,
} from "@/lib/seo";
import { z } from "zod";

import AudiolabTourContainer from "@/components/audiolab/tour/AudiolabTourContainer";
import { PersonaPickerContainer } from "@/components/persona-picker/PersonaPickerContainer";
import { setPersonaPickerSuppressed } from "@/components/persona-picker/usePersonaPickerVisibility";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    analytics.page();
  }, [location.href]);
  return null;
}

function Identifier() {
  const { data: currentUser, isAdmin } = useLoggedInUser();

  const name =
    `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim();
  const email = currentUser?.email;
  const userId = currentUser?.userId;
  const userbackInitialized = useRef(false);
  const { updateUserAsync } = useStatsigUser();

  // Handle cookie consent on mount
  useEffect(() => {
    CookieConsent.run(COOKIE_CONSENT_CONFIG);
  }, []);

  // Callback function to properly initialize or identify user in Userback
  const userbackIdentify = useCallback(async () => {
    if (!import.meta.env.VITE_USERBACK_TOKEN) return;

    const options = userId
      ? {
          user_data: {
            id: userId,
            info: {
              name,
              email,
              isAdmin,
            },
          },
        }
      : undefined;

    const existing = getUserbackInstance();

    if (existing) {
      if (userId) {
        existing.identify(userId, options?.user_data?.info || {});
      }
    }

    if (!existing && !userbackInitialized.current) {
      userbackInitialized.current = true;
      const instance = await Userback(
        import.meta.env.VITE_USERBACK_TOKEN!,
        options,
      );
      setUserbackInstance(instance);
    }
  }, [userId, email, name, isAdmin]);

  // TODO: Handle resetting when user session ends without a full page reload
  // e.g., when the token expires and the user is logged out automatically
  useEffect(() => {
    if (!(currentUser?.userId && currentUser?.email)) return;

    // TODO: API should return "name"

    const email = currentUser.email;
    const id = currentUser.userId;

    analytics.identify(id, {
      email,
      name,
      isAdmin,
    });

    datadogRum.setUser({
      id,
      name,
      email,
      isAdmin,
    });

    updateUserAsync({
      userID: id,
      email,
      custom: {
        isAdmin,
      },
    });
  }, [currentUser?.userId, currentUser?.email, name, isAdmin, updateUserAsync]);

  // Identify user in Userback when userbackIdentify changes
  useEffect(() => {
    userbackIdentify();
  }, [userbackIdentify]);

  return null;
}

// Saves the most recent route to local storage for use after login redirects
function RouteSaver() {
  const location = useLocation();
  const publicHref = location.publicHref;
  const searchStr = location.searchStr;
  useEffect(() => {
    const hasPendingSubscription = Boolean(
      localStorage.getItem("pending_subscription"),
    );
    setPersonaPickerSuppressed("pending-subscription", hasPendingSubscription);

    // Stripe sends buyers back with a session_id, and both fulfillment pages
    // strip it once the purchase settles — so it marks exactly the window where
    // picking a persona would navigate away mid-fulfillment.
    const hasCheckoutSession = Boolean(
      (location.search as Record<string, unknown>).session_id,
    );
    setPersonaPickerSuppressed("checkout-return", hasCheckoutSession);

    saveMostRecentRoute(location);
  }, [publicHref, searchStr]);

  return null;
}

declare global {
  interface Window {
    prerenderReady?: boolean;
  }
}

const LayoutContent = () => {
  const { currentTrack } = useAudioPlayerState();
  const { queue, hydrated, restoredFromStorage } = useAudioPlayerQueue();
  const pathname = useLocation({ select: (l) => l.pathname });

  // Signal Prerender.io that the page is ready to be captured
  useEffect(() => {
    window.prerenderReady = true;
  }, []);

  // Reset scroll to the top on every route change.
  // We hit both the in-app .main-scroll-container (the primary scroller on
  // desktop) and window/document — on some mobile browsers (Android Chrome
  // with a dynamic toolbar) the document itself can end up scrolling instead
  // of the inner container, and scrolling only the container is a no-op there.
  useEffect(() => {
    const container = document.querySelector(".main-scroll-container");
    container?.scrollTo({ top: 0, left: 0, behavior: "instant" });
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  // Check if AudioPlayer is displaying (PlayerBar or EmptyPlayer)
  const isAudioPlayerVisible =
    currentTrack !== null ||
    (hydrated && queue.length === 0 && restoredFromStorage);

  return (
    <>
      <HeadContent />
      <GlobalToastObserver />
      <PageViewTracker />
      <Identifier />
      <RouteSaver />
      {/* Fixed app shell - never scrolls */}
      <div className="h-screen w-full flex flex-col bg-page">
        <ImpersonationBanner />
        <HeaderNav />
        <CartDrawer />
        <AuthModal />
        <PolicyConsentModal />
        <EmailVerificationGateContainer />
        <PolicyUpdateBanner />
        {/* Isolated scroll container - only this scrolls */}
        <div
          className={`flex-1 main-scroll-container ${
            isAudioPlayerVisible ? "pb-[72px]" : ""
          }`}
        >
          <div className="min-h-full flex flex-col">
            <div className="flex-1">
              <Outlet />
              <TanStackRouterDevtools />
            </div>
          </div>
        </div>
      </div>
      {/* Fixed player - outside scroll context */}
      <AudioPlayer />
      <PersonaPickerContainer />
      <AudiolabTourContainer />
    </>
  );
};

const RootLayout = () => {
  return (
    <Providers>
      <LayoutContent />
    </Providers>
  );
};

const globalSearchParamsSchema = z.object({
  "success-toast": z.string().optional(),
  "info-toast": z.string().optional(),
  "open-cart": z.string().optional(),
  auth: z
    .enum(["login", "signup", "forgot-password", "verify-code"])
    .optional(),
  // Cross-page intent: "open the profile editor on arrival". Carried in the
  // URL rather than app state so it is route-scoped and self-clearing — a
  // navigation that never lands simply discards it.
  edit: z.enum(["profile"]).optional(),
  // QA hook: force the policy re-consent modal open even after accepting.
  preview: z.enum(["policy-consent"]).optional(),
});

export const Route = createRootRoute({
  validateSearch: (search) => globalSearchParamsSchema.parse(search),
  component: RootLayout,
  // Site-wide defaults. Every tag here is overridden by the deepest match that
  // declares the same name/property, so routes only restate what differs.
  // Deliberately no canonical: links from every active match are concatenated
  // rather than deduplicated, so canonicals belong on leaf routes only.
  head: () =>
    seoHead({
      title: SITE_TITLE,
      description: SITE_DESCRIPTION,
      jsonLd: [organizationJsonLd(), websiteJsonLd()],
    }),
});
